import { Text } from "@fluentui/react-components";
import { InfoRegular } from "@fluentui/react-icons";
import { isBefore } from "date-fns";
import { useAtomValue } from "jotai";
import { EApprovalStatus, EBenefitContentTab, EFilterStatusType } from "library/types";
import { useTranslation } from "react-i18next";
import { selectedCategoryFilterAtom, selectedStatusFilterAtom } from "store";
import { benefitContentTabAtom } from "store/UIBenefitsPage";
import styled from "styled-components";

import { BenefitCard } from "components/BenefitCard/BenefitCard";
import { CardListWrap } from "components/styled";
import { useBenefitsData } from "hooks/useBenefitsData";

export function EligiblePackages() {
  const { t } = useTranslation();
  const { eligibleFilteredData, packageData } = useBenefitsData();
  const eligiblePackages = packageData?.eligiblePackages || [];
  const enrolledPackages = packageData?.enrolledPackages || [];
  const selectedTabContent = useAtomValue(benefitContentTabAtom);
  const categoryFilters = useAtomValue(selectedCategoryFilterAtom);
  const statusFilters = useAtomValue(selectedStatusFilterAtom);

  const selectedContentTab = useAtomValue(benefitContentTabAtom);

  const enrolledPackagesImplementations = enrolledPackages.reduce(
    (acc, item) => {
      const implementationKey = item.benefitImplementationTag || "";
      const employeeKey = item.isDependent
        ? item.employeeDependentTag
        : item.employeeLoginEmailAddress;
      const dateOfCancellation = item?.dateOfCancellation || "";

      if (dateOfCancellation) {
        // if (isBefore(new Date(dateOfCancellation), new Date())) {
        //   return acc;
        // }
        return acc;
      }

      if (item.isDependent) {
        acc.dependent[employeeKey || ""] = {
          ...acc.dependent[employeeKey || ""],
          [implementationKey]: item,
        };
      } else {
        acc.employee[employeeKey || ""] = {
          ...acc.employee[employeeKey || ""],
          [implementationKey]: item,
        };
      }

      return acc;
    },
    { employee: {}, dependent: {} } // Initialize with employee and dependent objects
  );

  const packagesImplementationsOfPendingEnrollments = [
    ...eligiblePackages,
    ...enrolledPackages,
  ].reduce(
    (acc, item) => {
      const implementationKey = item.benefitImplementationTag || "";
      const employeeKey = item.isDependent
        ? item.employeeDependentTag
        : item.employeeLoginEmailAddress;
      const dateOfCancellation = item?.dateOfCancellation || "";

      if (dateOfCancellation) {
        if (isBefore(new Date(dateOfCancellation), new Date())) {
          return acc;
        }
      }
      const isInPendingStatus =
        item.approveEnrollStatusCode === EApprovalStatus.PENDING ||
        item.cancelEnrollApprovalStatusCode === EApprovalStatus.PENDING;

      if (!isInPendingStatus) return acc;

      if (item.isDependent) {
        acc.dependent[employeeKey || ""] = {
          ...acc.dependent[employeeKey || ""],
          [implementationKey]: item,
        };
      } else {
        acc.employee[employeeKey || ""] = {
          ...acc.employee[employeeKey || ""],
          [implementationKey]: item,
        };
      }

      return acc;
    },
    { employee: {}, dependent: {} } // Initialize with employee and dependent objects
  );

  return (
    <>
      {eligibleFilteredData?.length ? (
        <CardListWrap>
          {eligibleFilteredData
            .filter((benefit) => {
              const categoryFilterPassed = categoryFilters[selectedContentTab]?.find(
                (filter) => Number(filter.id) === benefit.benefitCategoryTag
              );

              const requiresEnrollmentApproval = benefit?.requiresEnrollmentApproval;

              const hasPendingStatus = benefit?.approveEnrollStatusCode === EApprovalStatus.PENDING;
              const waitingForApproval =
                requiresEnrollmentApproval && !!benefit?.dateOfEnrollment && hasPendingStatus;

              const inReviewFilterSelected = statusFilters[selectedContentTab]?.find(
                (filter) => filter.id === EFilterStatusType.IN_REVIEW
              );

              const inReviewFilterPassed = inReviewFilterSelected ? waitingForApproval : true;

              return categoryFilterPassed && inReviewFilterPassed;
            })
            .map((benefit) => {
              const upgradeData = (() => {
                const packageUser = benefit.isDependent ? "dependent" : "employee";
                const implementationKey = benefit.benefitImplementationTag || "";
                const employeeKey = benefit.isDependent
                  ? benefit.employeeDependentTag
                  : benefit.employeeLoginEmailAddress;

                const enrolledData =
                  enrolledPackagesImplementations?.[packageUser]?.[employeeKey || ""]?.[
                    implementationKey
                  ];

                return {
                  hasUpgrade: !!enrolledData && benefit.isUpgradeable,
                  enrolledPackageName: enrolledData?.name,
                };
              })();

              const packageFromSameImplementationInPendingStatusConfig = (() => {
                const packageUser = benefit.isDependent ? "dependent" : "employee";
                const implementationKey = benefit.benefitImplementationTag || "";
                const employeeKey = benefit.isDependent
                  ? benefit.employeeDependentTag
                  : benefit.employeeLoginEmailAddress;

                const packageInPendingStatus =
                  packagesImplementationsOfPendingEnrollments?.[packageUser]?.[employeeKey || ""]?.[
                    implementationKey
                  ];

                return {
                  hasPendingStatus: !!packageInPendingStatus,
                  packageInPendingStatus: packageInPendingStatus?.name,
                };
              })();

              const hasUpgradeAction = upgradeData?.hasUpgrade;
              const hasPackageFromSameImplementationInPendingStatus =
                packageFromSameImplementationInPendingStatusConfig?.hasPendingStatus;
              const enrolledPackageName = upgradeData?.enrolledPackageName || "";

              const requiresEnrollmentApproval = benefit?.requiresEnrollmentApproval;
              // const requiresCancellationApproval =
              //   benefit?.requiresCancellationApproval;

              const hasPendingStatus = benefit?.approveEnrollStatusCode === EApprovalStatus.PENDING;

              const waitingForApproval =
                requiresEnrollmentApproval && !!benefit?.dateOfEnrollment && hasPendingStatus;

              return (
                <BenefitCard
                  waitingForApproval={waitingForApproval}
                  {...(hasUpgradeAction
                    ? {
                        upgradeState: {
                          enrolledPackageName,
                          upgradePackageName: benefit?.name || "",
                        },
                      }
                    : {})}
                  {...(hasPackageFromSameImplementationInPendingStatus
                    ? {
                        packageFormSameImplementationsInPendingStatus:
                          packageFromSameImplementationInPendingStatusConfig.packageInPendingStatus,
                      }
                    : {})}
                  eligible
                  key={benefit.benefitPackageTag}
                  benefitPackage={benefit}
                />
              );
            })}
        </CardListWrap>
      ) : selectedTabContent === EBenefitContentTab.eligible ? (
        <NoEligibleBenefitsText>
          <div>
            <InfoRegular fontSize={18} />
          </div>
          <Text weight="semibold" block>
            {" "}
            {t("hbh.noEligibleBenefits.label")}
          </Text>
        </NoEligibleBenefitsText>
      ) : null}
    </>
  );
}

const NoEligibleBenefitsText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  max-width: 600px;
`;
